import React, { useEffect, useMemo, useState } from 'react';
import { Col, Form, Input, Row, Select } from 'antd';
import type { FormInstance } from 'antd';
import PhoneInput from 'react-phone-input-2';
import ArrowDown from '@/assets/icons/arrow-down-role.svg';
import CustomTitle from '@/components/CustomTitle.jsx';
import {
  CustomInput,
  AddItemButton,
  ToggleSwitch,
  CustomButton,
  CustomShipmentText,
} from '@/components';

import Flag from '../DirectContactList/Flag';
import PreferredChannelSelector, {
  PreferredChannel,
} from './PreferredChannelSelector';
import { PhoneCountryData, HoursRow } from './types';

type KeyedHoursRow = HoursRow & { id: number };
let nextRowId = 0;
const newRowId = () => {
  nextRowId += 1;
  return nextRowId;
};
import { selectValueStyle } from './styles';
import styles from './DirectoryContactEditScreen.module.css';
import {
  dayOptions,
  inlineGap,
  rowsToOperatingHours,
  timeOptions,
  zones,
} from './utils';

type Channel = 'phone' | 'email';

export interface DirectoryEditContactScreenProps {
  contact?: {
    id: string | number;
    name?: string;
    email?: string;
    phone?: string;
    country?: string;
    hours?: string;
    api?: {
      label?: string;
      topic?: string;
      country_code?: string;
      country_region?: string;
      operating_hours?: string;
      phone_number?: string;
      formatted_phone?: string;
      usage_notes?: string;
      preferred_channel?: PreferredChannel | 'none';
      timezone?: string;
      hours_24_7?: boolean | string;
    };
  } | null;
  onCancel?: () => void;
  onSave?: (payload: unknown) => void;
  form?: FormInstance;
}

const DirectoryEditContactScreen: React.FC<DirectoryEditContactScreenProps> = ({
  contact,
  onCancel,
  onSave,
  form: controlledForm,
}) => {
  const [internalForm] = Form.useForm();
  const form = controlledForm ?? internalForm;
  const initialPreferred: PreferredChannel =
    (contact?.api?.preferred_channel as PreferredChannel) ??
    (contact?.email ? 'email' : 'phone');

  const [channel, setChannel] = useState<Channel>(
    initialPreferred === 'email' ? 'email' : 'phone'
  );
  const seedRows: KeyedHoursRow[] = (() => {
    const op = contact?.api?.operating_hours ?? contact?.hours ?? '';
    if (op && op !== '24/7') {
      return [
        {
          id: newRowId(),
          startDay: 'Mon',
          endDay: 'Fri',
          startTime: '10:00',
          endTime: '17:00',
        },
      ];
    }
    return [
      {
        id: newRowId(),
        startDay: 'Mon',
        endDay: 'Fri',
        startTime: '10:00',
        endTime: '17:00',
      },
    ];
  })();

  const [rows, setRows] = useState<KeyedHoursRow[]>(seedRows);

  const initialIs247 =
    contact?.api?.hours_24_7 === true ||
    contact?.api?.hours_24_7 === 'true' ||
    contact?.api?.operating_hours === '24/7' ||
    contact?.hours === '24/7';

  const [is247, setIs247] = useState<boolean>(!!initialIs247);

  const phoneVal = Form.useWatch('phone_number', form);
  const tzVal = Form.useWatch('timezone', form);
  const opHoursVal = Form.useWatch('operating_hours', form);
  const hours247Val = Form.useWatch('hours_24_7', form);
  const preferredChannelVal = Form.useWatch('preferred_channel', form);

  useEffect(() => {
    const next = (preferredChannelVal ??
      initialPreferred ??
      'phone') as PreferredChannel;
    if (next === 'phone' || next === 'email') {
      setChannel(next);
    }
    form.setFieldsValue({ preferred_channel: next });
  }, [preferredChannelVal, initialPreferred, form]);

  useEffect(() => {
    const h247 = Boolean(hours247Val) || opHoursVal === '24/7';
    setIs247(h247);
    if (!tzVal) {
      form.setFieldsValue({ timezone: contact?.api?.timezone ?? 'GMT' });
    }
  }, [form, tzVal, opHoursVal, hours247Val, contact?.api?.timezone]);

  useEffect(() => {
    const tz = form.getFieldValue('timezone');
    const nextOp = is247 ? '24/7' : rowsToOperatingHours(rows, tz);
    form.setFieldsValue({
      hours_24_7: is247,
      operating_hours: nextOp,
    });
  }, [is247, rows, form]);
  useEffect(() => {
    form.setFieldsValue({
      country_code: contact?.api?.country_code ?? '',
      country_region:
        (contact?.api?.country_region ?? contact?.country ?? '') || '',
      preferred_channel: initialPreferred ?? 'phone',
      hours_24_7: initialIs247,
      operating_hours: initialIs247
        ? '24/7'
        : (contact?.api?.operating_hours ?? contact?.hours ?? ''),
      label: contact?.api?.label ?? contact?.name ?? '',
      email: contact?.email ?? '',
      phone_number:
        contact?.phone ??
        contact?.api?.formatted_phone ??
        contact?.api?.phone_number ??
        '',
      timezone: contact?.api?.timezone ?? 'GMT',
      notes: contact?.api?.usage_notes ?? '',
    });
  }, [contact, form, initialIs247, initialPreferred]);

  const addRow = () =>
    setRows((r) => [
      ...r,
      {
        id: newRowId(),
        startDay: null,
        endDay: null,
        startTime: null,
        endTime: null,
      },
    ]);
  const removeAllRows = () => setRows([]);

  const handleFinish = (values: Record<string, unknown>) => {
    const payload = {
      ...values,
      id: contact?.id,
      preferred_channel:
        values?.preferred_channel ?? initialPreferred ?? channel,
      hours_24_7: is247,
      operating_hours: is247
        ? '24/7'
        : rowsToOperatingHours(rows, values?.timezone as string),
    };
    onSave?.(payload);
  };

  const channelSpecificField = useMemo(() => {
    if (channel === 'phone') {
      return (
        <>
          <Form.Item
            label='Phone number'
            name='phone_number'
            rules={[{ required: true, message: 'Please enter phone number' }]}
            style={{ marginBottom: 16 }}
          >
            <CustomInput
              height='36px'
              bgColor='var(--light-gray)'
              border='0px'
              borderRadius='8px'
            >
              <PhoneInput
                country={'us'}
                value={phoneVal || ''}
                inputStyle={{
                  height: 36,
                  width: '100%',
                  border: 'none',
                  background: 'transparent',
                  paddingLeft: 48,
                }}
                buttonStyle={{
                  background: 'transparent',
                  borderTopLeftRadius: 8,
                  borderBottomLeftRadius: 8,
                  border: 'none',
                }}
                onChange={(
                  value,
                  data: PhoneCountryData,
                  _event,
                  formattedValue
                ) => {
                  form.setFieldsValue({
                    phone_number: formattedValue || value,
                    email: '',
                    country_code: data?.dialCode ? `+${data.dialCode}` : '',
                    country_region: data?.countryCode
                      ? String(data.countryCode).toUpperCase()
                      : '',
                  });
                }}
              />
            </CustomInput>
          </Form.Item>
        </>
      );
    }

    return (
      <>
        <div className={styles.emailLabel}>
          Email <span className={styles.required}>*</span>
        </div>
        <Form.Item
          name='email'
          rules={[
            { required: true, message: 'Please enter email' },
            { type: 'email' as const, message: 'Please enter a valid email' },
          ]}
          style={{ marginBottom: 16 }}
        >
          <CustomInput
            placeholder='name@company.com'
            height='36px'
            border='0px'
            bgColor='var(--light-gray)'
            borderRadius='8px'
          />
        </Form.Item>
      </>
    );
  }, [channel, phoneVal, form]);

  return (
    <div className={styles.screen}>
      <div className={styles.header}>
        <div className={styles.headerRow}>
          <div className={styles.headerText}>
            <CustomTitle
              style={{ position: 'relative', left: '-5px' }}
              text={'Update general support contact'}
              fontSize='20px'
              lineHeight='28px'
              fontWeight='700'
              marginBottom='0px'
            />
            {contact?.email && (
              <CustomTitle
                text={contact.email}
                fontSize='12px'
                marginBottom={0}
                lineHeight='16px'
                fontWeight={400}
                color='var(--text-secondary)'
                style={{ marginTop: 2 }}
              />
            )}
            {(contact?.phone ||
              contact?.api?.formatted_phone ||
              contact?.api?.phone_number) && (
              <div className={styles.phoneLine}>
                {(contact?.country ||
                  contact?.api?.country_region ||
                  contact?.api?.country_code) && (
                  <Flag
                    country={
                      contact?.country ??
                      contact?.api?.country_region ??
                      contact?.api?.country_code ??
                      ''
                    }
                  />
                )}
                <CustomTitle
                  text={
                    contact?.phone ??
                    contact?.api?.formatted_phone ??
                    contact?.api?.phone_number ??
                    ''
                  }
                  fontSize='12px'
                  marginBottom={0}
                  lineHeight='16px'
                  fontWeight={400}
                  color='var(--text-secondary)'
                />
              </div>
            )}
            {(contact?.hours || contact?.api?.operating_hours) && (
              <CustomTitle
                text={contact?.hours ?? contact?.api?.operating_hours ?? ''}
                fontSize='12px'
                marginBottom={0}
                lineHeight='16px'
                fontWeight={400}
                color='var(--text-secondary)'
                style={{ marginTop: 2 }}
              />
            )}
          </div>
        </div>
      </div>

      <div className={styles.body}>
        <Form
          form={form}
          layout='vertical'
          onFinish={handleFinish}
          initialValues={{ timezone: contact?.api?.timezone ?? 'GMT' }}
          style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
          requiredMark={false}
        >
          <Form.Item name='country_code' hidden>
            <input type='hidden' />
          </Form.Item>
          <Form.Item name='country_region' hidden>
            <input type='hidden' />
          </Form.Item>
          <Form.Item name='preferred_channel' hidden>
            <input type='hidden' />
          </Form.Item>
          <Form.Item name='hours_24_7' hidden>
            <input type='hidden' />
          </Form.Item>
          <Form.Item name='operating_hours' hidden>
            <input type='hidden' />
          </Form.Item>

          <div className={styles.fieldGroup}>
            <Form.Item
              className='custom-input form-input-style'
              label='Label'
              name='label'
            >
              <CustomInput
                placeholder='i.e., Main, Dangerous Goods, Express Freight, Billing'
                height='36px'
                border='0px'
                bgColor='var(--light-gray)'
                borderRadius='8px'
              />
            </Form.Item>
          </div>

          <PreferredChannelSelector
            title='Channel'
            value={preferredChannelVal ?? channel ?? 'phone'}
            onChange={(val: PreferredChannel) => {
              form.setFieldsValue({ preferred_channel: val });
              if (val === 'phone' || val === 'email') {
                setChannel(val);
                if (val === 'phone') {
                  form.setFieldsValue({ email: '' });
                } else {
                  form.setFieldsValue({
                    phone_number: '',
                    country_code: '',
                    country_region: '',
                  });
                }
              }
            }}
            isDirect={false}
            style={{ marginTop: -4, marginBottom: 8 }}
          />

          {channelSpecificField}

          <div className={styles.hoursHeader}>
            <div className={styles.label}>Operating hours</div>
            <div className={styles.toggleWrap}>
              <CustomShipmentText text='24/7' lineHeight='20px' />
              <ToggleSwitch
                checked={is247}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const checked = e.target.checked;
                  setIs247(checked);
                  if (checked) removeAllRows();
                  const tz = form.getFieldValue('timezone');
                  form.setFieldsValue({
                    hours_24_7: checked,
                    operating_hours: checked
                      ? '24/7'
                      : rowsToOperatingHours(rows, tz),
                  });
                }}
              />
            </div>
          </div>

          <div className={styles.rowsWrap}>
            {!is247 &&
              rows.map((row, idx) => (
                <Row
                  key={row.id}
                  gutter={inlineGap}
                  style={{ marginBottom: 8 }}
                  align='middle'
                >
                  <Col>
                    <Select
                      placeholder='Mon'
                      value={row.startDay ?? undefined}
                      onChange={(val: string) => {
                        const next = [...rows];
                        next[idx].startDay = val;
                        setRows(next);
                      }}
                      options={dayOptions}
                      suffixIcon={
                        <img
                          src={ArrowDown}
                          width={12}
                          height={12}
                          alt='dropdown arrow'
                          className={styles.arrowIcon}
                        />
                      }
                      className='select36'
                      style={{
                        ...selectValueStyle,
                        background: 'var(--light-gray)',
                        borderRadius: 8,
                        width: 76,
                        height: 36,
                      }}
                      disabled={is247}
                    />
                  </Col>

                  <Col
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: inlineGap / 2,
                    }}
                  >
                    <span className={styles.toLabel}>to</span>
                    <Select
                      placeholder='Fri'
                      value={row.endDay ?? undefined}
                      onChange={(val: string) => {
                        const next = [...rows];
                        next[idx].endDay = val;
                        setRows(next);
                      }}
                      options={dayOptions}
                      suffixIcon={
                        <img
                          src={ArrowDown}
                          width={12}
                          height={12}
                          alt='dropdown arrow'
                          className={styles.arrowIcon}
                        />
                      }
                      className='select36'
                      style={{
                        ...selectValueStyle,
                        background: 'var(--light-gray)',
                        borderRadius: 8,
                        width: 76,
                        height: 36,
                      }}
                      disabled={is247}
                    />
                  </Col>

                  <Col>
                    <Select
                      placeholder='10am'
                      value={row.startTime ?? undefined}
                      onChange={(val: string) => {
                        const next = [...rows];
                        next[idx].startTime = val;
                        setRows(next);
                      }}
                      options={timeOptions}
                      suffixIcon={
                        <img
                          src={ArrowDown}
                          width={12}
                          height={12}
                          alt='dropdown arrow'
                          className={styles.arrowIcon}
                        />
                      }
                      className='select36'
                      style={{
                        ...selectValueStyle,
                        background: 'var(--light-gray)',
                        borderRadius: 8,
                        width: 76,
                        height: 36,
                      }}
                      disabled={is247}
                    />
                  </Col>

                  <Col
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: inlineGap / 2,
                    }}
                  >
                    <span className={styles.toLabel}>to</span>
                    <Select
                      placeholder='5pm'
                      value={row.endTime ?? undefined}
                      onChange={(val: string) => {
                        const next = [...rows];
                        next[idx].endTime = val;
                        setRows(next);
                      }}
                      options={timeOptions}
                      suffixIcon={
                        <img
                          src={ArrowDown}
                          width={12}
                          height={12}
                          alt='dropdown arrow'
                          className={styles.arrowIcon}
                        />
                      }
                      className='select36'
                      style={{
                        ...selectValueStyle,
                        background: 'var(--light-gray)',
                        borderRadius: 8,
                        width: 76,
                        height: 36,
                      }}
                      disabled={is247}
                    />
                  </Col>
                </Row>
              ))}

            {!is247 && (
              <AddItemButton
                gap='24px'
                width='28px'
                height='28px'
                marginTop='0px'
                text='Add days'
                fontSize='12px'
                fontWeight='600'
                onClick={addRow}
              />
            )}
          </div>

          <Form.Item
            label='Timezone'
            name='timezone'
            style={{ marginBottom: 16, marginTop: 16 }}
          >
            <Select
              showSearch
              placeholder='Select Time Zone'
              className='custom-dd'
              optionFilterProp='label'
              options={zones.map((zone) => ({ label: zone, value: zone }))}
              style={{ width: '100%' }}
              onChange={(tz: string) => {
                form.setFieldsValue({
                  operating_hours: is247
                    ? '24/7'
                    : rowsToOperatingHours(rows, tz),
                });
              }}
            />
          </Form.Item>

          <Form.Item label='Note' name='notes' style={{ marginTop: 16 }}>
            <Input.TextArea
              rows={3}
              placeholder=''
              style={{
                height: '120px',
                paddingTop: 10,
                resize: 'none',
                background: 'var(--light-gray)',
                border: 'none',
                boxShadow: 'none',
              }}
              value={form.getFieldValue('notes')}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                const v = e.target.value;
                form.setFieldsValue({ notes: v });
              }}
            />
          </Form.Item>
        </Form>
      </div>

      <div className={styles.footer}>
        <CustomButton
          text='Save'
          bgColor='#000'
          color='#fff'
          borderRadius='8px'
          fontSize='16px'
          fontWeight='600'
          height='48px'
          width='100%'
          onClick={() => form.submit()}
        />
        <CustomButton
          boxShadow='none'
          text='Cancel'
          bgColor='transparent'
          color='var(--text-primary)'
          borderRadius='8px'
          fontSize='16px'
          fontWeight='400'
          height='auto'
          width='100%'
          border='none'
          marginTop='20px'
          marginBottom='10px'
          onClick={onCancel}
        />
      </div>
    </div>
  );
};

export default DirectoryEditContactScreen;
